import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  BankIdSessionResponse,
  InitiateBankIdRequest,
  InitiateBankIdResponse,
} from "../slices/bankid.slice";
import {
  getBankIdMockScenario,
  isMockSessionId,
  mockInitiateBankId,
  mockVerifyBankIdSession,
} from "@/lib/bankid-mock";
import { isLocalhost } from "@/lib/utils";
import { isErrorCode } from "@/lib/error-messages";

export const initiateBankId = createAsyncThunk<
  InitiateBankIdResponse,
  InitiateBankIdRequest,
  { rejectValue: string }
>(
  "bankid/initiateBankId",
  async (data, { rejectWithValue }) => {
    const scenario = getBankIdMockScenario();
    if (isLocalhost() && scenario) {
      if (scenario === "init_failed") {
        return rejectWithValue("FAILED_TO_INITIATE_BANKID");
      }
      const mockResult = mockInitiateBankId(data, data.callbackUrl);
      if (mockResult) {
        return mockResult;
      }
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/bankid/init`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...data,
          callbackUrl: encodeURI(data.callbackUrl),
        }),
      }
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const err = body?.error;
      const errorCode =
        typeof err === "string" && isErrorCode(err) ? err : "FAILED_TO_INITIATE_BANKID";
      return rejectWithValue(errorCode);
    }

    return response.json() as Promise<InitiateBankIdResponse>;
  }
);

export const verifyBankIdSession = createAsyncThunk<
  BankIdSessionResponse,
  string,
  { rejectValue: string }
>(
  "bankid/verifyBankIdSession",
  async (sessionId, { rejectWithValue }) => {
    if (isLocalhost() && isMockSessionId(sessionId)) {
      const mockResult = mockVerifyBankIdSession(sessionId);
      if ("errorCode" in mockResult) {
        return rejectWithValue(mockResult.errorCode);
      }
      return mockResult;
    }

    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/api/bankid/session/${sessionId}`
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const err = body?.error;
      const errorCode =
        typeof err === "string" && isErrorCode(err) ? err : "FAILED_TO_VERIFY_BANKID";
      return rejectWithValue(errorCode);
    }

    return response.json() as Promise<BankIdSessionResponse>;
  }
);
